import { useEffect, useState } from 'react';
import { dbGetBlob, dbGetThumb } from '@/lib/mediaDb';

/**
 * Resolves a locally-stored media blob to an object URL for rendering,
 * revoking it on unmount or when the target changes.
 *
 * Object URLs are a manual-memory API: every `createObjectURL` pins its
 * blob in memory until `revokeObjectURL` is called. A media grid mounts
 * and unmounts dozens of these while scrolling and filtering, so leaking
 * them would hold every image the user has ever viewed in memory for the
 * lifetime of the page. Centralizing the create/revoke pair in one hook
 * is what keeps that correct.
 *
 * @param sha256 The item to load, or undefined to load nothing.
 * @param variant `'thumb'` for the small generated preview (grids),
 *   `'blob'` for the full-size original (detail views, playback).
 */
export function useMediaObjectUrl(
  sha256: string | undefined,
  variant: 'thumb' | 'blob' = 'thumb',
): { url: string | undefined; isLoading: boolean; missing: boolean } {
  const [url, setUrl] = useState<string>();
  const [isLoading, setIsLoading] = useState(Boolean(sha256));
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!sha256) {
      setUrl(undefined);
      setIsLoading(false);
      setMissing(false);
      return;
    }

    let objectUrl: string | undefined;
    let cancelled = false;

    setIsLoading(true);
    setMissing(false);

    const load = variant === 'thumb' ? dbGetThumb(sha256) : dbGetBlob(sha256);

    load
      .then((blob) => {
        // The effect may have been cleaned up while awaiting. Creating
        // the URL anyway would leak it, since the cleanup already ran.
        if (cancelled) return;
        if (!blob) {
          setMissing(true);
          setUrl(undefined);
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setMissing(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [sha256, variant]);

  return { url, isLoading, missing };
}
